import {
	ErrorModal,
	HelmetDexteel,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Group, Panel, Separator } from "react-resizable-panels";

import { ATHENS_GRAY } from "@/shared/colors";
import { getProductArea, Product } from "@/shared/models/Product";
import {
	getProductDetail,
	getProducts,
} from "@/shared/repositories/ProductRepository";
import { EditSpecsModal } from "./components/EditSpecsModal";
import {
	ALL_AREAS,
	PRODUCT_FILTERS_INITIAL,
	ProductFilters,
	type ProductFiltersState,
} from "./components/ProductFilters";
import { ProductDetailPanel } from "./components/ProductDetailPanel";
import { ProductsTable } from "./components/ProductsTable";

export const ProductsPage = () => {
	const { state } = useUTLSettingsContext();
	const defaultAreaId = Number(get(state, "settings.DefaultAreaId", 10));
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { productId: productIdParam } = useParams<{ productId?: string }>();
	const selectedProductId = productIdParam ? Number(productIdParam) : null;

	const [error, setError] = useState("");
	const [filters, setFilters] = useState<ProductFiltersState>(
		PRODUCT_FILTERS_INITIAL,
	);
	const [editProductId, setEditProductId] = useState<number | null>(null);
	const [editInitialAreaId, setEditInitialAreaId] =
		useState<number>(defaultAreaId);

	const handleRowClicked = useCallback(
		(productId: number | null) => {
			if (productId) {
				navigate(`/products/${productId}`);
			} else {
				navigate("/products");
			}
		},
		[navigate],
	);

	const handleNavigateChain = useCallback(
		(productId: number) => {
			navigate(`/products/${productId}`);
		},
		[navigate],
	);

	const handleEdit = useCallback((productId: number, areaId: number) => {
		setEditProductId(productId);
		setEditInitialAreaId(areaId);
	}, []);

	const { data: products, isLoading } = useQuery<Product[], Error>({
		queryKey: ["products"],
		queryFn: () => getProducts(),
		onError: (e: Error) => setError(e.message),
	});

	const filteredProducts = useMemo(() => {
		if (!products) return [];
		const allAreasSelected = filters.areas.length === ALL_AREAS.length;
		const areaSet = new Set(filters.areas);
		return products.filter((p) => {
			if (filters.customerId && p.CustomerId !== filters.customerId)
				return false;
			if (filters.sectionId && p.SectionId !== filters.sectionId) return false;
			if (filters.gradeId && p.GradeId !== filters.gradeId) return false;
			if (!allAreasSelected) {
				const area = getProductArea(p);
				if (!area || !areaSet.has(area)) return false;
			}
			if (filters.orphansOnly) {
				const area = getProductArea(p);
				if (!area || area === "saw" || p.ParentProductId != null) return false;
			}
			return true;
		});
	}, [products, filters]);

	const { data: productDetail, isLoading: isLoadingDetail } = useQuery<
		Product,
		Error
	>({
		queryKey: ["productDetail", selectedProductId],
		queryFn: () => getProductDetail(selectedProductId!, defaultAreaId),
		enabled: selectedProductId !== null,
		onError: (e: Error) => setError(e.message),
	});

	return (
		<>
			<HelmetDexteel title="Products" />
			<Box
				sx={{
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
						Products Catalog
					</Typography>
					<ProductFilters filters={filters} onChange={setFilters} />
				</Box>

				<Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
					<Group orientation="horizontal" style={{ height: "100%" }}>
						<Panel id="products-table" defaultSize={55} minSize={25}>
							<ProductsTable
								isLoading={isLoading}
								rows={filteredProducts}
								quickFilterText={filters.quickFilterText}
								selectedProductId={selectedProductId}
								defaultAreaId={defaultAreaId}
								onRowClicked={handleRowClicked}
								onEdit={handleEdit}
							/>
						</Panel>

						<Separator
							style={{
								width: 8,
								backgroundColor: "#e0e0e0",
								cursor: "col-resize",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Box
								sx={{
									width: 4,
									height: 40,
									backgroundColor: "#bdbdbd",
									borderRadius: 2,
								}}
							/>
						</Separator>

						<Panel id="product-detail" defaultSize={45} minSize={20}>
							<Paper elevation={2} sx={{ height: "100%", overflow: "hidden" }}>
								<ProductDetailPanel
									product={productDetail ?? null}
									isLoading={isLoadingDetail && selectedProductId !== null}
									onEdit={handleEdit}
									onNavigateProduct={handleNavigateChain}
								/>
							</Paper>
						</Panel>
					</Group>
				</Box>
			</Box>
			<EditSpecsModal
				productId={editProductId}
				initialAreaId={editInitialAreaId}
				onClose={(shouldRefresh) => {
					setEditProductId(null);
					if (shouldRefresh) {
						queryClient.invalidateQueries({ queryKey: ["products"] });
						queryClient.invalidateQueries({ queryKey: ["productDetail"] });
					}
				}}
				onNavigateProduct={(id) => handleNavigateChain(id)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
